import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getCollection, api } from "../config";
import moment from "moment";

export const addExistingEvent = createAsyncThunk(
  "addExisting/addExistingEvent",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { account, addExisting } = getState();
      const { event, locations, groupName, orgType, dates, user, selectionMode} = addExisting;
      
      const brandID = locations[0].brandID;

      let data = {
        eventUserID: account.user.parentUserID ? account.user.parentUserID : account.user.uid,
        brandID: brandID,
        eventDates:dates.map(d=>d.type==='range'?`${d.start}-${d.end}`:`${d.start}`),
        eventID:event.id,
        user:user,
        orgType:orgType.label,
        digital:event.digital||false,
        multiDay:selectionMode==='range'?true:false,
        invite:true
      };

      if(locations.length===1) data.locationID = locations[0].value;
      else {
        data.locationIDs = locations.map(l=>l.value);
        data.groupName = groupName;
      }
      if(event.digital) data.digital = true;
      const rs = await api.eventReservationCreate(data);
      return rs;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getEvents = createAsyncThunk(
    "addExisting/getEvents",
    async (_, { getState, rejectWithValue }) => {
  
      const { account, addExisting } = getState();
      const { user } = account;
      const { locations, selectionMode } = addExisting;
  
      try {
        
        const brandID = locations[0].brandID;

        let events = [];
        if(locations.length==1){
            events = await getCollection("events", {
                where: [["locationIDs." + locations[0].value, "==", true]],
            });
            events = events.filter((d) => {
              let expired = false;  
              let maxUnixDate = 0;
              if(d.eventDates) maxUnixDate = Math.max.apply(Math, d.eventDates.map(d=>d.start));
              if(!d.perpetual&&moment().isAfter(moment.unix(maxUnixDate))) expired = true;
              return !d.deleted && d.active && !expired;
            });
            let override = events.filter((d) => d.override);
            if (override.length === 0) {
                let defaults = await getCollection("events", {
                    where: [
                      ["userID", "==", user.parentUserID||user.uid],
                      ["default", "==", true],
                    ],
                });
                defaults.forEach((d) =>
                    d.brandID === brandID ? events.push({...d,name:d.name+" (Default)"}) : ""
                );
            }
        }else{
            events = await getCollection("events", {
                where: [
                    [
                        "userID",
                        "==",
                        (user.parentUserID ? user.parentUserID : user.id)
                    ]
                ],
            });
            events = events.filter((e) => {
              let expired = false;  
              let maxUnixDate = 0;
              if(e.eventDates) maxUnixDate = Math.max.apply(Math, e.eventDates.map(d=>d.start));
              if(!e.perpetual&&moment().isAfter(moment.unix(maxUnixDate))) expired = true;
              return e.brandID === brandID && !e.deleted && !e.override && e.active && !expired;
            });
        }
        
    
        return events.filter(e=>selectionMode==='range'?e.multiDay:!e.multiDay).sort((a, b) =>
            a.name > b.name ? 1 : b.name > a.name ? -1 : 0
        );
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

export const getLocations = createAsyncThunk(
  "addExisting/getLocations",
  async (_, { getState, rejectWithValue }) => {

    const { account } = getState();
    const { user } = account;

    try {
        let doc = await getCollection("locations", {
            where: [
            [
                "users." + (user.parentUserID ? user.parentUserID : user.id),
                "==",
                true,
            ],
            ],
            joins: [
            {
                key: "brandID",
                collection: "brands",
                field: "brand",
            },
            ],
        });
    
        let locations = doc.filter(
            l =>
            !l.deleted &&
            ![
                "Non Participating",
                "Temporarily Closed",
                "Scheduled",
                "Referral",
            ].includes(l.status)
        ).map((location) => {
            return {
                value: location.id,
                label: location.location,
                brandName: location.brand.brand,
                timezone: location.timezone || "America/Chicago",
                schedule: location.schedule ? location.schedule : {},
                brandID: location.brandID,
            };
        });
        return locations.sort((a, b) =>
            a.label > b.label ? 1 : b.label > a.label ? -1 : 0
        );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getOrgTypes = createAsyncThunk(
  "addExisting/getOrgTypes",
  async (_, { getState, rejectWithValue }) => {
    const { account, addExisting } = getState();
    const { user } = account;
    const { locations } = addExisting;
    try {
      let rs = await getCollection("org_types", {where: [["userID", "==", user.uid]]});
      if(rs.length===0&&locations.length===1) rs = await getCollection("org_types", {where: [["brandID", "==", locations[0].brandID]]});
      if(rs.length===0) rs = await getCollection("org_types", {where: [["userID", "==", false],["brandID", "==", false]]});
      return rs.map((d) => ({
        value: d.id,
        label: d.type,
        ...d,
      })).sort((a, b) => a.label.localeCompare(b.label));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addExistingSlice = createSlice({
  name: "addExisting",
  initialState: {
    step: 1,
    error: null,
    selectionMode: 'single',
    dates: [],
    locations: [],
    locationOptions: [],
    loadingLocations: false,
    eventOptions: [],
    loadingEvents: false,
    event: null,
    groupName: '',
    user: null,
    orgType: null,
    orgTypes: [],
    loadingOrgTypes: false,
    processing: false
  },
  reducers: {
    // Step navigation
    setStep: (state, action) => {
      state.step = action.payload;
    },
    setDates: (state, action) => {
      state.dates = action.payload;
    },
    setSelectionMode: (state, action) => {
      state.selectionMode = action.payload;
    },
    setLocations: (state, action) => {
      state.locations = action.payload;
    },
    setEvent: (state, action) => {
      state.event = action.payload;
    },
    setGroupName: (state, action) => {
      state.groupName = action.payload;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setOrgType: (state, action) => {
      state.orgType = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    resetState: (state, action) => {
      state.step = 1;
      state.error = null;
      state.selectionMode = 'single';
      state.dates = [];
      state.locations = [];
      state.locationOptions = [];
      state.event = null;
      state.eventOptions = [];
      state.groupName = '';
      state.user = null;
      state.orgType = null;
      state.orgTypes = [];
      state.loadingOrgTypes = false;
      state.loadingLocations = false;
      state.loadingEvents = false;
      state.loadingOrgTypes = false;
      state.loadingEvents = false;
    }
  },
  extraReducers: {
    [getLocations.fulfilled]: (state, action) => {
      state.locationOptions = action.payload;
      state.loadingLocations = false;
    },
    [getLocations.pending]: (state, action) => {
      state.loadingLocations = true;
    },
    [getLocations.rejected]: (state, action) => {
      state.loadingLocations = false;
    },
    [getEvents.fulfilled]: (state, action) => {
      state.eventOptions = action.payload;
      state.loadingEvents = false;
    },
    [getEvents.pending]: (state, action) => {
      state.loadingEvents = true;
    },
    [getEvents.rejected]: (state, action) => {
      state.loadingEvents = false;
    },
    [getOrgTypes.fulfilled]: (state, action) => {
      state.orgTypes = action.payload;
      state.loadingOrgTypes = false;
    },
    [getOrgTypes.pending]: (state, action) => {
      state.loadingOrgTypes = true;
    },
    [getOrgTypes.rejected]: (state, action) => {
      state.loadingOrgTypes = false;
    },
    [addExistingEvent.fulfilled]: (state, action) => {
      state.processing = false;
      state.step++;
    },
    [addExistingEvent.pending]: (state, action) => {
      state.processing = true;
      state.error = null;
    },
    [addExistingEvent.rejected]: (state, action) => {
      state.processing = false;
      state.error = action.payload;
    },
  },
});

export const {
  // Step navigation
  setStep,
  setDates,
  setSelectionMode,
  setLocations,
  setEvent,
  setGroupName,
  setUser,
  setOrgType,
  setError,
  resetState
} = addExistingSlice.actions;

export default addExistingSlice.reducer;